import React, {useContext, useEffect, useState} from "react";
import {Button, Col, Row, Select} from "antd";
import {AppContext} from "../../contexts/app-context";
import {useLocation} from "react-router-dom";
import RegionSelect from "./region-select";
import SelectField from "../select-field";
import {ACCESS_TO_ALL_DELIVERIES_ITEMS, ACCESS_TO_ANY_DELIVERIES_ITEMS} from "../../helpers/user_permission.helper";
import {consignorService, cookieConsentService, userServices} from "../../services";

import i18n from "../../i18n";

export default function TopFilter() {
  const {Option} = Select;
  const appContext = useContext(AppContext);
  const [clearFilterFlag, setClearFilterFlag] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  const path = (
    useLocation()
      .pathname
      .split("/")
      .filter(Boolean)
      .pop() || "root"
  );

  const {regionsLoadings, regionsUnloadings} = appContext.filter;

  const getFilterCookieName = (name) => `${name}-${path}`;

  const readFilterCookie = (name) => {
    const storedValue = cookieConsentService.getFunctionalCookie(
      getFilterCookieName(name)
    );

    if (!storedValue) {
      return {};
    }

    return storedValue
      .split(",")
      .reduce((result, item) => {
        const separatorIndex = item.indexOf(": ");

        if (separatorIndex === -1) {
          return result;
        }

        const key = item.slice(0, separatorIndex);
        const rawValue = item.slice(separatorIndex + 2);

        if (!key) {
          return result;
        }

        result[key] = rawValue
          ? rawValue
            .split("|")
            .map(Number)
            .filter(Number.isFinite)
          : [];

        return result;
      }, {});
  };

  const hasFilterValues = (values) => {
    return Object.values(values).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return (
        value !== null &&
        value !== undefined &&
        value !== ""
      );
    });
  };

  const writeFilterCookie = (name, values) => {
    const cookieName = getFilterCookieName(name);

    if (!values || !hasFilterValues(values)) {
      cookieConsentService.removeFunctionalCookie(cookieName);
      return;
    }

    const serializedValue = Object.entries(values)
      .map(([key, value]) => {
        const normalizedValue = Array.isArray(value)
          ? value.join("|")
          : String(value);

        return `${key}: ${normalizedValue}`;
      })
      .join(",");

    cookieConsentService.setFunctionalCookie(
      cookieName,
      serializedValue
    );
  };

  const removeFilterCookie = (name) => {
    cookieConsentService.removeFunctionalCookie(
      getFilterCookieName(name)
    );
  };

  const [selectedRegionsLoading, setSelectedRegionsLoading] = useState(
    () => readFilterCookie("regionsLoading")
  );

  const [selectedRegionsUnloading, setSelectedRegionsUnloading] = useState(
    () => readFilterCookie("regionsUnloading")
  );

  const [selectedSorting, setSelectedSorting] = useState(
    appContext.filter.selectedSorting
  );

  const [consignors, setConsignors] = useState(null);

  const user = userServices.getUser();

  const isAccessToAllDeliveries = (
    user.isAccessTo(ACCESS_TO_ALL_DELIVERIES_ITEMS) &&
    user.isAccessTo(ACCESS_TO_ANY_DELIVERIES_ITEMS)
  );

  const getStoredConsignorIds = (name) => {
    if (!isAccessToAllDeliveries) {
      return [];
    }

    const storedValue = readFilterCookie(name);

    if (!Array.isArray(storedValue.data)) {
      return [];
    }

    return storedValue.data
      .map(Number)
      .filter(Number.isFinite);
  };

  const [selectedConsignors, setSelectedConsignors] = useState(
    () => getStoredConsignorIds("selectedConsignors")
  );

  const [
    selectedExcludedConsginors,
    setExcludedConsignors,
  ] = useState(
    () => getStoredConsignorIds("excludedConsignors")
  );

  const isEmptyFilter = !Boolean(Object.keys(selectedRegionsLoading).length) && !Boolean(Object.keys(selectedRegionsUnloading).length) && selectedSorting === 'default' && selectedConsignors.length === 0
    &&  selectedExcludedConsginors.length === 0;



  const onChangeRegionLoading = (values) => {
    setSelectedRegionsLoading(values);
  };
  const onChangeRegionUnloading = (values) => {
    setSelectedRegionsUnloading(values);
  };
  const onChangeFilterSort = (value) => {
    setSelectedSorting(value);
  };

  useEffect(() => {
    if ((!isFiltered && !isEmptyFilter) || clearFilterFlag) {
      if (selectedConsignors.length > 0) {
        appContext.applyFilterParams(selectedRegionsLoading, selectedRegionsUnloading, selectedSorting, selectedConsignors, [], false);
      } else if (selectedExcludedConsginors.length > 0) {
        appContext.applyFilterParams(selectedRegionsLoading, selectedRegionsUnloading, selectedSorting, [], selectedExcludedConsginors, true);
      }
      if (!isFiltered && !isEmptyFilter) {
        setIsFiltered(true);
        setClearFilterFlag(true);
      }
    }
  }, [isFiltered, clearFilterFlag]);

  const applyFilterParams = () => {
    let isExcludeConsignors = false;
    if (selectedExcludedConsginors.length > 0) {
      isExcludeConsignors = true;
    }

    appContext.applyFilterParams(selectedRegionsLoading, selectedRegionsUnloading, selectedSorting, selectedConsignors, selectedExcludedConsginors, isExcludeConsignors);

    if (!isEmptyFilter) {
      setIsFiltered(true);
      setClearFilterFlag(true);
    }

    writeFilterCookie("regionsLoading", selectedRegionsLoading);
    writeFilterCookie("regionsUnloading", selectedRegionsUnloading);
    writeFilterCookie("selectedConsignors", {data: selectedConsignors});
    writeFilterCookie("excludedConsignors", {data: selectedExcludedConsginors});
  };

  const clearFilterParams = () => {
    setSelectedRegionsUnloading({});
    setSelectedRegionsLoading({});
    setSelectedSorting('default');
    setSelectedConsignors([]);
    setExcludedConsignors([]);

    removeFilterCookie("regionsLoading");
    removeFilterCookie("regionsUnloading");
    removeFilterCookie("selectedConsignors");
    removeFilterCookie("excludedConsignors");
    if (isFiltered) {
      setIsFiltered(false);
    }
  };

  const sortItems = appContext.filter.sort;

  useEffect(() => {
    if (isAccessToAllDeliveries && consignors === null) {
      consignorService.getConsignorsList()
        .then((response) => {
          let formatted = response.map((item) => {
            return {
              id: item.Id,
              name: item.Name
            };
          })
          setConsignors(formatted);
        })
        .catch((response) => {

        })
    }
  }, [isAccessToAllDeliveries])




  return (
    <div className="filters-container">
      <div className="container">
        <div className="filters-container__wrapper">
          <Row gutter={22} style={{padding: "0", margin: "0", rowGap: "10px"}} type="flex" justify="start">
            <Col className="first-filter" xs={12} sm={12} md={8} lg={8} xl={8} style={{}}>
              <div style={{position: 'relative', height: '100%'}} id="regionsLoadings">
                <RegionSelect
                  regions={regionsLoadings}
                  prefix={'regionsLoadings'}
                  placeholder={i18n.t('components.filter.regionsLoadings')}
                  onChange={onChangeRegionLoading}
                  defaultValue={selectedRegionsLoading}
                />
              </div>
            </Col>
            <Col className="first-filter" xs={12} sm={12} md={8} lg={8} xl={8} style={{}}>
              <div style={{position: 'relative', height: '100%'}} id="regionsUnloadings">
                <RegionSelect
                  regions={regionsUnloadings}
                  prefix={'regionsUnloadings'}
                  placeholder={i18n.t('components.filter.regionsUnloadings')}
                  onChange={onChangeRegionUnloading}
                  defaultValue={selectedRegionsUnloading}
                />
              </div>
            </Col>
            <Col xs={12} sm={12} md={5} lg={5} xl={5} style={{}}>
              <Select
                size="default"
                onChange={(value) => {
                  onChangeFilterSort(value);
                }}
                defaultValue={selectedSorting}
                value={selectedSorting}
                style={{width: "100%", height: "100%"}}
              >
                {sortItems.map((sortItem) => {
                  return (
                    <Option key={sortItem.name} value={sortItem.name} selected={sortItem.selected}>
                      {sortItem.title}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col className="filters-buttons" xs={12} sm={12} md={3} lg={3} xl={3} style={{}}>
              <Button
                className="filters-button"
                type="primary"
                size="default"
                icon="search"
                htmlType="button"
                title={i18n.t('components.filter.buttons.apply')}
                onClick={applyFilterParams}
              />
              <Button
                className="filters-button filters-button--clear"
                type="primary"
                size="default"
                icon="close"
                disabled={isEmptyFilter}
                htmlType="button"
                title={i18n.t('components.filter.buttons.clear')}
                style={{border: "none", outline: "none", marginLeft: "5px"}}
                onClick={clearFilterParams}
              />
            </Col>
          </Row>
          { isAccessToAllDeliveries ?
          <Row gutter={22} style={{padding: "0", margin: "0", rowGap: "10px"}} type="flex" justify="start">
            <Col  xs={12} sm={12} md={8} lg={8} xl={8} style={{}}>
                <SelectField
                  mode='multiple'
                  name='consignors'
                  placeholder={i18n.t('components.filter.consignors.placeholder')}
                  values={consignors}
                  selectedValue={selectedConsignors}
                  onChange={(val) => {
                    setExcludedConsignors([]);
                    setSelectedConsignors(val);
                  }}
                  getErrors={()=>{}}
                  notFoundText={i18n.t('components.filter.consignors.notFoundText')}
                  style={{width: "100%",  marginTop: "15px"}}
                  allowClear={true}
                />

            </Col>
           <Col  xs={12} sm={12} md={8} lg={8} xl={8} style={{}}>
                <SelectField
                  mode='multiple'
                  name='excludedConsignors'
                  placeholder={i18n.t('components.filter.excludedConsignors.placeholder')}
                  values={consignors}
                  selectedValue={selectedExcludedConsginors}
                  onChange={(val) => {
                    setSelectedConsignors([]);
                    setExcludedConsignors(val);
                  }}
                  getErrors={()=>{}}
                  notFoundText={i18n.t('components.filter.excludedConsignors.notFoundText')}
                  style={{width: "100%",  marginTop: "15px"}}
                  allowClear={true}
                />

            </Col>
          </Row>
          : null
          }
        </div>
      </div>
    </div>
  );
}
